import { useEffect, useState } from 'react';
import { AlertCircle, Edit, Loader2, PieChart } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  apiService,
  ApiError,
  CriterionHookUsage,
  StandardEvaluationCriterion,
} from '../../services/api';
import { isCriterionInUseError } from './evaluation-criteria/criterion-in-use';
import { CategoryChipInput } from './evaluation-criteria/CategoryChipInput';
import {
  CriterionFormErrors,
  CriterionFormState,
  PROMPT_MAX_LENGTH,
  validateCriterionForm,
} from './evaluation-criteria/criterion-form';

/**
 * Pflege der Standard-Auswertungen des qualitativen Reportings (CA-762).
 * Änderungen gelten für alle Bots ab dem nächsten ausgewerteten Gespräch;
 * im Customer Portal sind die Standard-Auswertungen schreibgeschützt.
 */
export function StandardEvaluationCriteriaPage() {
  const [criteria, setCriteria] = useState<StandardEvaluationCriterion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<StandardEvaluationCriterion | null>(null);
  const [form, setForm] = useState<CriterionFormState>({ name: '', prompt: '', categories: [], isActive: true });
  const [errors, setErrors] = useState<CriterionFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [blockingUsages, setBlockingUsages] = useState<CriterionHookUsage[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setCriteria(await apiService.getStandardEvaluationCriteria());
    } catch (err) {
      setError((err as ApiError).message ?? 'Standard-Auswertungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openEditor = (criterion: StandardEvaluationCriterion) => {
    setEditing(criterion);
    setForm({
      name: criterion.name,
      prompt: criterion.prompt,
      categories: [...criterion.categories],
      isActive: criterion.isActive,
    });
    setErrors({});
    setSaveError(null);
    setBlockingUsages([]);
  };

  const save = async () => {
    if (!editing) return;
    const nextErrors = validateCriterionForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setIsSaving(true);
      setSaveError(null);
      setBlockingUsages([]);
      const updated = await apiService.updateStandardEvaluationCriterion(editing.id, {
        name: form.name.trim(),
        prompt: form.prompt,
        categories: form.categories,
        isActive: form.isActive,
      });
      setCriteria((list) => list.map((c) => (c.id === updated.id ? updated : c)));
      setEditing(null);
      toast.success('Standard-Auswertung gespeichert');
    } catch (err) {
      if (isCriterionInUseError(err)) {
        // CA-663: Kategorie entfernt bzw. Auswertung deaktiviert, obwohl Hooks sie nutzen.
        setBlockingUsages(err.details.usages);
      } else {
        setSaveError((err as ApiError).message ?? 'Speichern fehlgeschlagen');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const promptLength = form.prompt.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Standard-Auswertungen</h1>
        <p className="text-gray-600">
          Default-Auswertungen des qualitativen Reportings für alle Bots. Transkript, erlaubte Kategorien und
          Ausgabeformat ergänzt der Hintergrund-Prompt (System-Richtlinien → „Qualitative Auswertung“).
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {criteria.map((criterion) => (
            <Card key={criterion.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <PieChart className="h-4 w-4 text-gray-500" />
                    {criterion.name}
                    {!criterion.isActive && <Badge variant="outline">Global inaktiv</Badge>}
                  </CardTitle>
                  {criterion.systemKey && <CardDescription className="font-mono text-xs">{criterion.systemKey}</CardDescription>}
                </div>
                <Button variant="outline" size="sm" onClick={() => openEditor(criterion)}>
                  <Edit className="mr-2 h-4 w-4" /> Bearbeiten
                </Button>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1.5">
                {criterion.categories.map((category) => (
                  <Badge key={category} variant="secondary">{category}</Badge>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Standard-Auswertung bearbeiten</DialogTitle>
            <DialogDescription>Gilt für alle Bots ab dem nächsten ausgewerteten Gespräch.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="criterion-name">Bezeichnung</Label>
              <Input
                id="criterion-name"
                value={form.name}
                maxLength={255}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={errors.name ? 'border-red-500' : undefined}
              />
              {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="criterion-prompt">Prompt (Auswertungslogik)</Label>
              <Textarea
                id="criterion-prompt"
                rows={12}
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                className={`font-mono text-xs ${errors.prompt ? 'border-red-500' : ''}`}
              />
              <div className="flex justify-between text-xs">
                <span className="text-red-600">{errors.prompt}</span>
                <span className={promptLength >= PROMPT_MAX_LENGTH * 0.9 ? 'font-medium text-amber-600' : 'text-gray-500'}>
                  {promptLength.toLocaleString('de-DE')} / {PROMPT_MAX_LENGTH.toLocaleString('de-DE')} Zeichen
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="criterion-categories">Kategorien</Label>
              <CategoryChipInput
                id="criterion-categories"
                value={form.categories}
                onChange={(categories) => setForm({ ...form, categories })}
                invalid={Boolean(errors.categories)}
              />
              <p className="text-xs text-gray-500">Die KI darf ausschließlich exakt diese Werte zurückgeben.</p>
              {errors.categories && <p className="text-xs text-red-600">{errors.categories}</p>}
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Aktiv (global)</p>
                <p className="text-xs text-gray-500">Inaktiv = wird bei keinem Bot mehr ausgewertet</p>
              </div>
              <Switch checked={form.isActive} onCheckedChange={(isActive) => setForm({ ...form, isActive })} />
            </div>

            {blockingUsages.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="flex items-center gap-2 font-medium">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Änderung nicht möglich: Die Auswertung bzw. Kategorie wird in Hooks verwendet.
                </p>
                <p className="mt-1 text-xs">
                  Erst wenn sie in keinem Hook mehr verwendet wird, kann sie entfernt bzw. deaktiviert werden.
                </p>
                <ul className="mt-2 space-y-1 text-xs">
                  {blockingUsages.map((usage) => (
                    <li key={usage.hookId}>
                      <span className="font-medium">{usage.customerName}</span> · {usage.agentName} · Hook „{usage.hookName}“
                      {usage.categories.length > 0 && <span className="text-amber-700"> ({usage.categories.join(', ')})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {saveError && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {saveError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={isSaving}>Abbrechen</Button>
            <Button onClick={() => void save()} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
